import { useState } from 'react'

import cn from 'clsx'

import { getValueWithCurrency } from '@App/helpers/toCurrency'
import { Trade } from '@App/redux/slices/trade/trades/types'
import { MarketIcon } from '@App/ui/crypto_exchange/desktop/components/common/MarketIcon'

import { Select } from '../../../../../SpotOrderTrade/components/Select'
import { useSeeMarket, useTrades } from '../../../../model'
import { Card } from '../Card'

import s from './styles.module.scss'

enum Result {
  Profit = 'Most profitable',
  Loss = 'Least profitable',
}
const options = [
  { name: Result.Profit, value: Result.Profit },
  { name: Result.Loss, value: Result.Loss },
]

type MarketGroup = {
  market: Trade['market']
  totalResult: number
  tradesCount: number
}

export const YourMarkets = () => {
  const [tradeResult, setTradeResult] = useState(Result.Profit)
  const { trades } = useTrades()

  const closedTrades = trades.filter((v) => v.status === 'closed')

  const groupedByMarket: Record<number, MarketGroup> = closedTrades.reduce(
    (acc: Record<number, MarketGroup>, trade) => {
      const marketId = trade.market.id

      if (!acc[marketId]) {
        acc[marketId] = {
          market: trade.market,
          totalResult: 0,
          tradesCount: 0,
        }
      }

      acc[marketId].totalResult += trade.result
      acc[marketId].tradesCount += 1

      return acc
    },
    {},
  )

  let marketGroups = Object.values(groupedByMarket)

  if (tradeResult === Result.Profit) {
    marketGroups = marketGroups.filter((g) => g.totalResult > 0)
    marketGroups.sort((a, b) => b.totalResult - a.totalResult)
  } else {
    marketGroups = marketGroups.filter((g) => g.totalResult < 0)
    marketGroups.sort((a, b) => a.totalResult - b.totalResult)
  }

  return (
    <Card
      className={s.container}
      title="Your Markets"
      contentClassName={s.content}
      headerRightElement={
        <Select
          value={tradeResult}
          options={options}
          containerClassName={s.selectContainer}
          inputClassName={s.select}
          optionClassName={s.option}
          onChange={(val) => setTradeResult(val as Result)}
        />
      }
    >
      <div className={s.header}>
        <div className={s.segment}>Market</div>

        <div className={cn(s.segment, s.end)}>Trades</div>
        <div className={cn(s.segment, s.end)}>Profit &amp; Loss</div>
      </div>

      <div className={s.trades}>
        {marketGroups.map((group) => (
          <TradeGroup key={group.market.id} group={group} />
        ))}
      </div>
    </Card>
  )
}
const TradeGroup = ({ group }: { group: MarketGroup }) => {
  const { seeMarket } = useSeeMarket({
    sourceCurrency: group.market.source,
    targetCurrency: group.market.target,
  })
  return (
    <div className={s.trade} key={group.market.id} onClick={() => seeMarket()}>
      <div className={cn(s.segment, s.left)}>
        <MarketIcon
          className={s.icon}
          source_currency={group.market.source}
          target_currency={group.market.target}
        />
        <div className={s.content}>
          <div className={s.name}>
            {group.market.source}/{group.market.target}
          </div>
        </div>
      </div>
      <div className={cn(s.segment, s.middle)}>
        <span className={s.badge}>{group.tradesCount}</span>
      </div>
      <div className={cn(s.segment, s.right, group.totalResult < 0 && s.negative)}>
        {getValueWithCurrency(group.market.target, group.totalResult)}
      </div>
    </div>
  )
}
